/**
 * BenchmarkPicker — reusable component for selecting benchmark comparison sources.
 *
 * Allows users to pick portfolio groups, hierarchy entities (branches, MAJCOMs,
 * offices), or "All DoW" as benchmark comparison sources. Used in both the
 * GoalEditorFull and ExecExplore pages.
 */
import { useState, useEffect, useMemo } from 'react'
import { X, Plus, Search, Globe, Building2, FolderOpen } from 'lucide-react'
import { govdataAPI } from '../../services/govdata'
import { execAPI } from '../../services/exec'
import type { BenchmarkSource, PortfolioGroup } from '../../types/exec'
import type { DimOffice } from '../../types/govdata'

interface Props {
  benchmarks: BenchmarkSource[]
  onChange: (benchmarks: BenchmarkSource[]) => void
  maxBenchmarks?: number
  /** Exclude this portfolio group from the portfolio picker (the goal's own group) */
  excludePortfolioGroupId?: string
}

type HierarchyLevel = 'sub_agency' | 'macom' | 'subcom1' | 'office'

const LEVEL_LABELS: Record<HierarchyLevel, string> = {
  sub_agency: 'Branch / COCOM',
  macom: 'MAJCOM',
  subcom1: 'Sub-Command',
  office: 'Office',
}

const BENCHMARK_COLORS = ['#8b5cf6', '#f97316', '#06b6d4', '#ec4899', '#14b8a6']

function nextColor(existing: BenchmarkSource[]): string {
  const usedColors = new Set(existing.map((b) => b.color))
  return BENCHMARK_COLORS.find((c) => !usedColors.has(c)) || BENCHMARK_COLORS[existing.length % BENCHMARK_COLORS.length]
}

function genId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `bm-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

interface EntityOption {
  code: string
  name: string
  level: HierarchyLevel
  officeCount: number
}

export default function BenchmarkPicker({
  benchmarks,
  onChange,
  maxBenchmarks = 5,
  excludePortfolioGroupId,
}: Props) {
  const [showAdd, setShowAdd] = useState(false)
  const [tab, setTab] = useState<'hierarchy' | 'portfolio'>('hierarchy')
  const [addLevel, setAddLevel] = useState<HierarchyLevel>('sub_agency')
  const [searchQuery, setSearchQuery] = useState('')
  const [dimOffices, setDimOffices] = useState<DimOffice[]>([])
  const [portfolioGroups, setPortfolioGroups] = useState<PortfolioGroup[]>([])

  useEffect(() => {
    govdataAPI.dimOffices().then((resp) => setDimOffices(resp.results)).catch(() => {})
    execAPI.getPortfolioGroups().then(setPortfolioGroups).catch(() => {})
  }, [])

  const existingKeys = useMemo(
    () => new Set(benchmarks.map((b) => `${b.type}:${b.entity_code ?? 'all'}`)),
    [benchmarks],
  )

  const entityOptions = useMemo((): EntityOption[] => {
    const map = new Map<string, EntityOption>()
    for (const o of dimOffices) {
      if (addLevel === 'sub_agency' && o.sub_agency_code && o.sub_agency_name) {
        const key = `sub_agency:${o.sub_agency_code}`
        if (!map.has(key)) map.set(key, { code: o.sub_agency_code, name: o.sub_agency_name, level: 'sub_agency', officeCount: 0 })
        map.get(key)!.officeCount++
      }
      if (addLevel === 'macom' && o.macom_code && o.macom_name) {
        const key = `macom:${o.macom_code}`
        if (!map.has(key)) map.set(key, { code: o.macom_code, name: o.macom_name, level: 'macom', officeCount: 0 })
        map.get(key)!.officeCount++
      }
      if (addLevel === 'subcom1' && o.subcom1_code && o.subcom1_name) {
        const key = `subcom1:${o.subcom1_code}`
        if (!map.has(key)) map.set(key, { code: o.subcom1_code, name: o.subcom1_name, level: 'subcom1', officeCount: 0 })
        map.get(key)!.officeCount++
      }
      if (addLevel === 'office' && o.office_code && o.office_name) {
        map.set(`office:${o.office_code}`, { code: o.office_code, name: o.office_name, level: 'office', officeCount: 1 })
      }
    }
    let options = Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name))
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      options = options.filter((o) => o.name.toLowerCase().includes(q) || o.code.toLowerCase().includes(q))
    }
    // Exclude already-added
    options = options.filter((o) => !existingKeys.has(`${o.level}:${o.code}`))
    return options.slice(0, 50)
  }, [dimOffices, addLevel, searchQuery, existingKeys])

  const filteredPortfolios = useMemo(() => {
    return portfolioGroups.filter((pg) => {
      if (pg.id === excludePortfolioGroupId) return false
      if (existingKeys.has(`portfolio_group:${pg.id}`)) return false
      if (searchQuery) {
        const q = searchQuery.toLowerCase()
        return pg.name.toLowerCase().includes(q)
      }
      return true
    })
  }, [portfolioGroups, excludePortfolioGroupId, existingKeys, searchQuery])

  const addBenchmark = (source: Omit<BenchmarkSource, 'id' | 'color'>) => {
    if (benchmarks.length >= maxBenchmarks) return
    const newBm: BenchmarkSource = {
      id: genId(),
      color: nextColor(benchmarks),
      ...source,
    }
    onChange([...benchmarks, newBm])
  }

  const removeBenchmark = (id: string) => {
    onChange(benchmarks.filter((b) => b.id !== id))
  }

  const atMax = benchmarks.length >= maxBenchmarks
  const hasAllDod = existingKeys.has('all_dod:all')

  const selectClass = 'w-full px-2 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500'

  return (
    <div>
      {/* Selected benchmarks */}
      {benchmarks.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {benchmarks.map((bm) => (
            <span
              key={bm.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: bm.color }} />
              {bm.label}
              <button
                onClick={() => removeBenchmark(bm.id)}
                className="ml-0.5 hover:text-red-500"
              >
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Add benchmark section */}
      {!atMax && (
        <>
          {!showAdd ? (
            <button
              onClick={() => setShowAdd(true)}
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400"
            >
              <Plus size={12} />
              Add benchmark comparison
            </button>
          ) : (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-2 space-y-2">
              {/* Quick add: All DoW */}
              {!hasAllDod && (
                <button
                  onClick={() => addBenchmark({ type: 'all_dod', entity_code: null, label: 'All DoW' })}
                  className="flex items-center gap-1.5 w-full px-2 py-1.5 text-xs rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800"
                >
                  <Globe size={12} />
                  All DoW (baseline)
                </button>
              )}

              {/* Tab toggle */}
              <div className="flex gap-1 border-b border-gray-100 dark:border-gray-800 pb-1">
                <button
                  onClick={() => { setTab('hierarchy'); setSearchQuery('') }}
                  className={`flex items-center gap-1 px-2 py-1 text-[10px] rounded-t ${
                    tab === 'hierarchy'
                      ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Building2 size={10} />
                  Hierarchy
                </button>
                <button
                  onClick={() => { setTab('portfolio'); setSearchQuery('') }}
                  className={`flex items-center gap-1 px-2 py-1 text-[10px] rounded-t ${
                    tab === 'portfolio'
                      ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <FolderOpen size={10} />
                  Portfolios
                </button>
              </div>

              {tab === 'hierarchy' && (
                <>
                  {/* Level selector */}
                  <select
                    value={addLevel}
                    onChange={(e) => { setAddLevel(e.target.value as HierarchyLevel); setSearchQuery('') }}
                    className={selectClass}
                  >
                    {(Object.keys(LEVEL_LABELS) as HierarchyLevel[]).map((lvl) => (
                      <option key={lvl} value={lvl}>{LEVEL_LABELS[lvl]}</option>
                    ))}
                  </select>

                  {/* Search */}
                  <div className="relative">
                    <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={`Search ${LEVEL_LABELS[addLevel]}...`}
                      className={`${selectClass} pl-6`}
                    />
                  </div>

                  {/* Entity list */}
                  <div className="max-h-32 overflow-y-auto space-y-0.5">
                    {entityOptions.length === 0 && (
                      <p className="text-[10px] text-gray-400 py-2 text-center">
                        {searchQuery ? 'No matches' : 'Loading...'}
                      </p>
                    )}
                    {entityOptions.map((opt) => (
                      <button
                        key={`${opt.level}:${opt.code}`}
                        onClick={() => addBenchmark({ type: opt.level, entity_code: opt.code, label: opt.name })}
                        className="flex items-center justify-between w-full px-2 py-1 text-xs rounded hover:bg-gray-50 dark:hover:bg-gray-800 text-left"
                      >
                        <span className="text-gray-900 dark:text-gray-100 truncate">{opt.name}</span>
                        <span className="text-[10px] text-gray-400 shrink-0 ml-2">{opt.officeCount} offices</span>
                      </button>
                    ))}
                  </div>
                </>
              )}

              {tab === 'portfolio' && (
                <>
                  {/* Search */}
                  <div className="relative">
                    <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search portfolio groups..."
                      className={`${selectClass} pl-6`}
                    />
                  </div>

                  <div className="max-h-32 overflow-y-auto space-y-0.5">
                    {filteredPortfolios.length === 0 && (
                      <p className="text-[10px] text-gray-400 py-2 text-center">
                        {searchQuery ? 'No matches' : 'No other portfolio groups'}
                      </p>
                    )}
                    {filteredPortfolios.map((pg) => (
                      <button
                        key={pg.id}
                        onClick={() => addBenchmark({ type: 'portfolio_group', entity_code: pg.id, label: pg.name })}
                        className="flex items-center justify-between w-full px-2 py-1 text-xs rounded hover:bg-gray-50 dark:hover:bg-gray-800 text-left"
                      >
                        <span className="text-gray-900 dark:text-gray-100 truncate">{pg.name}</span>
                        <span className="text-[10px] text-gray-400 shrink-0 ml-2">{pg.resolved_office_count} offices</span>
                      </button>
                    ))}
                  </div>
                </>
              )}

              <button
                onClick={() => setShowAdd(false)}
                className="text-[10px] text-gray-400 hover:text-gray-600"
              >
                Close
              </button>
            </div>
          )}
        </>
      )}
      {atMax && (
        <p className="text-[10px] text-gray-400">Maximum {maxBenchmarks} benchmarks reached.</p>
      )}
    </div>
  )
}

export { BENCHMARK_COLORS }
